'use client';

import Link from 'next/link';
import NextImage from 'next/image';
import type { ReactNode } from 'react';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import CmsImage from '@/components/ui/CmsImage';
import { HomeBuildingGuideItem } from '../data/guides';
import { getImageUrl, stripHtml } from '@/lib/homepage-mappers';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';
import { getStrapiMediaUrl, getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type { TransformedGuidesLandingData } from '@/types/guides-landing';

interface HomeBuildingGuideListProps {
  locale: string;
  guides: HomeBuildingGuideItem[];
  landingData?: TransformedGuidesLandingData | null;
  /**
   * Streaming slot for the FAQ + enquiry block — rendered by an async
   * Server Component wrapped in `<Suspense>` in `page.tsx`.
   */
  faqSlot?: ReactNode;
}

const FALLBACK_PHASE_CARDS = [
  {
    slug: 'home-construction-budget',
    title: 'Preparation',
    description: 'Lay the groundwork for a flawless construction process.',
    buttonText: 'Plan Your Construction',
    image: '/images/guid_gr.jpg',
    hoverImage: '/images/guid_gr_fl1.jpg',
  },
  {
    slug: 'home-construction-care',
    title: 'Execution',
    description: 'Turn your blueprint into a structure of uncompromising strength.',
    buttonText: 'Master the Build',
    image: '/images/guid_gr1.jpg',
    hoverImage: '/images/guid_gr_fl2.png',
  },
  {
    slug: 'planning-and-home-design',
    title: 'Protection',
    description: 'Armour your home against moisture, heat, and environmental wear.',
    buttonText: 'Secure Your Legacy',
    image: '/images/guid_gr.jpg',
    hoverImage: '/images/guid_gr_fl3.png',
  },
];

function buildPhaseHref(locale: string, buttonUrl: string | null | undefined): string {
  if (!buttonUrl) return `/${locale}/home-building-guide`;
  if (/^https?:\/\//.test(buttonUrl)) return buttonUrl;
  const trimmed = buttonUrl.startsWith('/') ? buttonUrl : `/${buttonUrl}`;
  return `/${locale}/home-building-guide${trimmed}`;
}

function buildToolHref(locale: string, pageUrl: string | null | undefined): string {
  if (!pageUrl) return `/${locale}`;
  if (/^https?:\/\//.test(pageUrl)) return pageUrl;
  const trimmed = pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`;
  return `/${locale}${trimmed}`;
}

const FALLBACK_TOOL_CARDS = [
  {
    heading: 'Construction Cost Calculator',
    subHeading:
      '<p>Get a data-driven estimate of your material requirements and total budget in seconds.</p>',
    bgColor: '#7f2222',
    textColor: '#FFFFFF',
    pageUrl: '/tools/construction-cost-calculator',
    iconUrl: '/images/coast_cal.svg',
    iconBg: 'rgba(255,255,255,0.1)',
  },
  {
    heading: 'Dealer Locator',
    subHeading:
      '<p>Find authorized Wonder Cement partners in your area to ensure genuine quality for your site.</p>',
    bgColor: '#F8F7F4',
    textColor: '#0A0A0A',
    pageUrl: '/tools/dealer-locator',
    iconUrl: '/images/Dealer_Locator.svg',
    iconBg: '#FFFFFF',
  },
];

type PhaseCard = {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  ariaLabel: string;
  image: string;
  hoverImage: string;
  href: string;
  linkProps?: ReturnType<typeof getCmsButtonLinkProps>;
  slug: string;
};

type ToolCard = {
  id: string;
  heading: string;
  subHeadingHtml: string;
  bgColor: string;
  textColor: string;
  href: string;
  iconUrl: string;
  iconAlt: string;
};

function buildPhaseCards(
  locale: string,
  phasesSection: TransformedGuidesLandingData['phases'] | null,
): PhaseCard[] {
  if (!phasesSection?.item) {
    return FALLBACK_PHASE_CARDS.map((card) => ({
      id: card.slug,
      title: card.title,
      description: card.description,
      buttonText: card.buttonText,
      ariaLabel: card.title,
      image: card.image,
      hoverImage: card.hoverImage,
      href: `/${locale}/home-building-guide/${card.slug}`,
      slug: card.slug,
    }));
  }

  return phasesSection.item.map((item, idx) => {
    const fallback = FALLBACK_PHASE_CARDS[idx] ?? FALLBACK_PHASE_CARDS[0];
    // Source assets are pre-compressed before upload; prefer the CMS original.
    const before = getStrapiMediaUrl(item.imageBefore as never, 'large');
    const after = getStrapiMediaUrl(item.imageAfter as never, 'large');
    const primary = before || after || fallback.image;
    const hover = after || before || fallback.hoverImage;
    const button = item.button?.[0];
    const href = buildPhaseHref(locale, button?.buttonUrl);

    return {
      id: String(item.id),
      title: item.heading || fallback.title,
      description: stripHtml(item.subHeading) || fallback.description,
      buttonText: button?.buttonText || fallback.buttonText,
      ariaLabel: button?.ariaLabel || item.heading || fallback.title,
      image: primary,
      hoverImage: hover,
      href,
      linkProps: getCmsButtonLinkProps(button),
      slug: button?.buttonUrl?.replace(/^\//, '') || fallback.slug,
    };
  });
}

function buildToolCards(
  locale: string,
  toolsSection: TransformedGuidesLandingData['tools'] | null,
): ToolCard[] {
  if (!toolsSection?.item) {
    return FALLBACK_TOOL_CARDS.map((card, idx) => ({
      id: `fallback-${idx}`,
      heading: card.heading,
      subHeadingHtml: card.subHeading,
      bgColor: card.bgColor,
      textColor: card.textColor,
      href: `/${locale}${card.pageUrl}`,
      iconUrl: card.iconUrl,
      iconAlt: card.heading,
    }));
  }

  return toolsSection.item.map((item, idx) => {
    const fallback = FALLBACK_TOOL_CARDS[idx] ?? FALLBACK_TOOL_CARDS[0];
    return {
      id: String(item.id),
      heading: item.heading || fallback.heading,
      subHeadingHtml: item.subHeading || fallback.subHeading,
      bgColor: item.bgColor || fallback.bgColor,
      textColor: item.textColor || fallback.textColor,
      href: buildToolHref(locale, item.pageUrl),
      iconUrl: getImageUrl(item.icon) || fallback.iconUrl,
      iconAlt: item.icon?.alternativeText || item.heading || fallback.heading,
    };
  });
}

function PhaseCardArticle({ card, guideTitle }: { card: PhaseCard; guideTitle?: string }) {
  return (
    <article className="group relative min-h-[280px] overflow-hidden rounded-[10px] text-white sm:min-h-[320px] lg:min-h-[420px] lg:rounded-none lg:text-black">
      <NextImage
        src={card.image}
        alt={guideTitle || card.title}
        fill
        sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
        className="object-cover object-center"
      />
      <NextImage
        src={card.hoverImage}
        alt=""
        aria-hidden
        fill
        sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
        className="object-cover object-center opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      />
      <div className="absolute inset-0 bg-black/40 transition-colors duration-500 lg:bg-black/0 lg:group-hover:bg-black/40" />

      <div className="relative flex h-full flex-col items-center justify-center px-5 py-10 text-center sm:px-6 hover:text-white">
        <h3 className="text-3xl font-semibold sm:text-4xl">{card.title}</h3>
        <p className="mt-3 max-w-[280px] text-sm leading-relaxed sm:max-w-[300px]">
          {card.description}
        </p>

        <SwapTextButton
          text={card.buttonText}
          hoverText={card.buttonText}
          href={card.href}
          {...card.linkProps}
          showArrow={false}
          className="mt-6 rounded-md px-4 py-2 text-xs sm:mt-7 sm:text-sm"
          aria-label={card.ariaLabel}
        />
      </div>
    </article>
  );
}

function ToolCardLink({ card }: { card: ToolCard }) {
  const isLightBg =
    card.bgColor?.toUpperCase() === '#F8F7F4' || card.bgColor?.toUpperCase() === '#FFFFFF';
  const iconBoxBg = isLightBg ? '#FFFFFF' : 'rgba(255,255,255,0.1)';
  const iconSize = card.iconUrl.endsWith('.svg') ? 28 : 22;

  return (
    <Link
      href={card.href}
      className="group flex items-start gap-3 rounded-lg px-4 py-4 transition-colors hover:opacity-90 sm:gap-4"
      style={{ backgroundColor: card.bgColor, color: card.textColor }}
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md sm:h-12 sm:w-12"
        style={{ backgroundColor: iconBoxBg }}
      >
        <NextImage src={card.iconUrl} alt={card.iconAlt} width={iconSize} height={iconSize} />
      </div>
      <div className="min-w-0">
        <h3
          className="text-xl font-semibold leading-tight sm:text-2xl"
          style={{ color: card.textColor }}
        >
          {card.heading}
        </h3>
        <div
          className="mt-1 text-sm leading-relaxed [&_p]:m-0"
          style={{ color: card.textColor, opacity: 0.85 }}
          dangerouslySetInnerHTML={{ __html: card.subHeadingHtml }}
        />
      </div>
    </Link>
  );
}

/** Resolve the landing hero/banner props (CMS values with hardcoded fallbacks). */
function resolveGuideLandingHero(landingData: TransformedGuidesLandingData | null | undefined) {
  const bannerMedia = landingData?.hero.bannerMedia ?? null;
  return {
    heroHeading: landingData?.hero.heading || 'Phase-Wise House\nConstruction Guide',
    heroTitleRichText: landingData?.hero.titleRichText ?? null,
    heroSubheadingHtml: landingData?.hero.subheadingHtml ?? null,
    bannerMedia,
    bannerUrl:
      getStrapiOriginalMediaUrl(bannerMedia) ||
      getImageUrl(bannerMedia) ||
      '/images/guide_img1.jpg',
    bannerAlt: bannerMedia?.alternativeText || 'Phase-wise home construction guide',
    bannerWidth: bannerMedia?.width || 1440,
    bannerHeight: bannerMedia?.height || 810,
  };
}

export default function HomeBuildingGuideList({
  locale,
  guides,
  landingData,
  faqSlot,
}: HomeBuildingGuideListProps) {
  const guideBySlug = new Map(guides.map((guide) => [guide.slug, guide]));

  const {
    heroHeading,
    heroTitleRichText,
    heroSubheadingHtml,
    bannerMedia,
    bannerUrl,
    bannerAlt,
    bannerWidth,
    bannerHeight,
  } = resolveGuideLandingHero(landingData);

  const phasesSection = landingData?.phases ?? null;
  const phasesHeading = phasesSection?.heading || 'Select Your Current Phase';
  const phasesSubheading =
    phasesSection?.subHeading ||
    '<p>Every strong home is built through three critical stages. Learn how to monitor house construction at each step, and your structure will stand the test of time.</p>';

  const cmsPhaseCards = buildPhaseCards(locale, phasesSection);

  const toolsSection = landingData?.tools ?? null;
  const toolsHeading = toolsSection?.heading || 'Project Planning\nTools';
  const toolsSubheading =
    toolsSection?.subHeading ||
    '<p>Leverage these expert tools to take the guesswork out of your construction journey.</p>';

  const cmsToolCards = buildToolCards(locale, toolsSection);

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <section className="overflow-hidden">
        <div className="md:hidden">
          <CmsImage
            src={bannerUrl}
            media={bannerMedia}
            alt={bannerAlt}
            width={bannerWidth}
            height={bannerHeight}
            priority
            loading="eager"
            sizes="100vw"
            className="h-auto w-full"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />

          <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-10">
            <div className="max-w-[660px] text-[#111827]">
              <CmsRichTextHeading
                className="hero-title whitespace-pre-line text-[#111827]"
                html={heroTitleRichText}
                fallback={heroHeading}
              />
              {heroSubheadingHtml ? (
                <div
                  className="hero-subtitle mt-4 max-w-[660px] text-[#252525] sm:mt-5 [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: heroSubheadingHtml }}
                />
              ) : (
                <p className="hero-subtitle mt-4 max-w-[660px] text-[#252525] sm:mt-5">
                  Explore our comprehensive library of home-building guides designed to walk you
                  through the technical essentials of every construction milestone.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="relative isolate hidden overflow-hidden md:block">
          <CmsImage
            src={bannerUrl}
            media={bannerMedia}
            alt={bannerAlt}
            fill
            priority
            loading="eager"
            sizes="100vw"
            className="object-cover object-center"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-white/10" />

          <div className="relative container mx-auto px-4 sm:px-6 lg:px-0">
            <div className="flex min-h-[620px] items-center py-16 h-[calc(100vh-200px)]">
              <div className="max-w-[660px] text-[#111827]">
                <CmsRichTextHeading
                  className="hero-title whitespace-pre-line text-[#111827]"
                  html={heroTitleRichText}
                  fallback={heroHeading}
                />
                {heroSubheadingHtml ? (
                  <div
                    className="hero-subtitle mt-5 max-w-[660px] text-[#252525] [&_p]:m-0"
                    dangerouslySetInnerHTML={{ __html: heroSubheadingHtml }}
                  />
                ) : (
                  <p className="hero-subtitle mt-5 max-w-[660px] text-[#252525]">
                    Explore our comprehensive library of home-building guides designed to walk you
                    through the technical essentials of every construction milestone.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`${landingData && !phasesSection ? 'hidden' : ''} py-12 md:py-16 max-sm:px-0 max-sm:py-0`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-0">
          <div className="mx-auto max-w-3xl text-center max-sm:text-left">
            <h2 className="whitespace-pre-line text-3xl font-semibold leading-tight text-[#111827] sm:text-4xl md:text-6xl">
              {phasesHeading}
            </h2>
            <div
              className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[#4b5563] md:text-base [&_p]:m-0"
              dangerouslySetInnerHTML={{ __html: phasesSubheading }}
            />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 overflow-hidden rounded-[10px] sm:gap-5 lg:grid-cols-3 lg:gap-0">
            {cmsPhaseCards.map((card) => (
              <PhaseCardArticle
                key={card.id}
                card={card}
                guideTitle={guideBySlug.get(card.slug)?.title}
              />
            ))}
          </div>
        </div>
      </section>
      <section
        className={`${landingData && !toolsSection ? 'hidden' : ''} py-10 md:py-14 max-sm:px-0 max-sm:py-4`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-0">
          <div className="rounded-xl bg-[#3a0004] px-5 py-6 sm:px-6 sm:py-7 md:px-10 md:py-9">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
              <div className="max-w-xl text-white">
                <h2 className="whitespace-pre-line text-2xl font-semibold leading-[1.1] sm:text-3xl md:text-4xl">
                  {toolsHeading}
                </h2>
                <div
                  className="mt-4 max-w-md text-sm leading-relaxed text-white/80 md:text-base [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: toolsSubheading }}
                />
              </div>

              <div className="w-full max-w-xl space-y-4">
                {cmsToolCards.map((card) => (
                  <ToolCardLink key={card.id} card={card} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ + Enquiry Form — streamed via Suspense from page.tsx so the
          lead-forms fetch doesn't block above-the-fold paint. */}
      {faqSlot}
    </div>
  );
}
